import { EnvironmentOutlined, LoadingOutlined } from '@ant-design/icons';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { getPlaceDetails, searchPlaces } from '../api/googlePlaces.js';

function GoogleMapsLocationPicker({ locationData, onChange }) {
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [latitude, setLatitude] = useState('');
    const [longitude, setLongitude] = useState('');
    const [mapLoading, setMapLoading] = useState(false);
    const [mapError, setMapError] = useState(null);
    const [dropdownPosition, setDropdownPosition] = useState(null);
    const inputContainerRef = useRef(null);
    const dropdownRef = useRef(null);
    const mapRef = useRef(null);
    const markerRef = useRef(null);
    const mapContainerRef = useRef(null);

    // Initialize from locationData prop
    useEffect(() => {
        if (locationData) {
            setLatitude(locationData.latitude || '');
            setLongitude(locationData.longitude || '');
        }
    }, [locationData]);

    // Initialize Google Map with interactive marker
    useEffect(() => {
        if (!latitude || !longitude || !mapContainerRef.current) {
            return;
        }

        setMapLoading(true);
        setMapError(null);

        const initializeMap = () => {
            try {
                // Double-check that Google Maps API is loaded
                if (!window.google || !window.google.maps) {
                    console.error('Google Maps API not loaded yet');
                    setMapError('Google Maps API failed to load');
                    setMapLoading(false);
                    return;
                }

                const lat = parseFloat(latitude);
                const lng = parseFloat(longitude);

                // Create map if it doesn't exist or if the container is empty
                if (!mapRef.current || !mapContainerRef.current.hasChildNodes()) {
                    console.log('Creating new map at', lat, lng);

                    // Clear any existing content in the container
                    if (mapContainerRef.current) {
                        mapContainerRef.current.innerHTML = '';
                    }

                    mapRef.current = new window.google.maps.Map(mapContainerRef.current, {
                        center: { lat, lng },
                        zoom: 15,
                        zoomControl: true,
                        mapTypeControl: true,
                        streetViewControl: false,
                        fullscreenControl: true,
                        gestureHandling: 'greedy', // Allows dragging without Ctrl key
                    });

                    // Create draggable marker
                    markerRef.current = new window.google.maps.Marker({
                        position: { lat, lng },
                        map: mapRef.current,
                        draggable: true,
                        title: 'Drag to adjust location',
                    });

                    // Update coordinates when marker is dragged
                    markerRef.current.addListener('dragend', (event) => {
                        const newLat = event.latLng.lat();
                        const newLng = event.latLng.lng();
                        setLatitude(newLat.toString());
                        setLongitude(newLng.toString());
                        onChange({
                            latitude: newLat,
                            longitude: newLng,
                        });
                    });

                    // Allow clicking on map to move marker
                    mapRef.current.addListener('click', (event) => {
                        const newLat = event.latLng.lat();
                        const newLng = event.latLng.lng();
                        markerRef.current.setPosition({ lat: newLat, lng: newLng });
                        setLatitude(newLat.toString());
                        setLongitude(newLng.toString());
                        onChange({
                            latitude: newLat,
                            longitude: newLng,
                        });
                    });
                } else {
                    // Update existing map and marker
                    console.log('Updating existing map to', lat, lng);
                    const newCenter = { lat, lng };
                    mapRef.current.setCenter(newCenter);
                    if (markerRef.current) {
                        markerRef.current.setPosition(newCenter);
                    }
                }

                setMapLoading(false);
            } catch (error) {
                console.error('Error initializing map:', error);
                setMapError(error.message);
                setMapLoading(false);
            }
        };

        // Load Google Maps API if not already loaded
        if (!window.google || !window.google.maps) {
            // Check if script is already being loaded
            const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
            if (existingScript) {
                // Script is loading, wait for it
                console.log('Waiting for existing Google Maps script to load...');
                existingScript.addEventListener('load', initializeMap);
                return () => {
                    existingScript.removeEventListener('load', initializeMap);
                };
            }

            // Load the script
            console.log('Loading Google Maps API script...');
            const script = document.createElement('script');
            script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_EMBED_KEY || 'AIzaSyA6zs572wXZY80NfwrGECA6tjzt_Dt0a2o'}`;
            script.async = true;
            script.defer = true;
            script.onload = () => {
                console.log('Google Maps API loaded successfully');
                initializeMap();
            };
            script.onerror = (error) => {
                console.error('Failed to load Google Maps API script:', error);
                setMapError('Failed to load Google Maps. Please check your API key.');
                setMapLoading(false);
            };
            document.head.appendChild(script);
        } else {
            // API already loaded
            console.log('Google Maps API already loaded');
            initializeMap();
        }

        // Cleanup function
        return () => {
            // Don't clean up the map/marker here since we want to reuse them
            // Only clean up event listeners if needed
        };
    }, [latitude, longitude, onChange]);

    // Cleanup on component unmount
    useEffect(() => {
        return () => {
            // Clean up map and marker when component unmounts
            if (markerRef.current) {
                markerRef.current.setMap(null);
                markerRef.current = null;
            }
            if (mapRef.current) {
                // Clear the map container to prevent DOM conflicts
                if (mapContainerRef.current) {
                    mapContainerRef.current.innerHTML = '';
                }
                mapRef.current = null;
            }
        };
    }, []);

    // Debounce search
    useEffect(() => {
        if (!searchQuery.trim() || searchQuery.length < 3) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        const timeoutId = setTimeout(async () => {
            try {
                setIsSearching(true);
                const results = await searchPlaces(searchQuery);
                setSuggestions(results);
                setShowSuggestions(true);
            } catch (error) {
                console.error('Error fetching suggestions:', error);
                setSuggestions([]);
            } finally {
                setIsSearching(false);
            }
        }, 500); // 500ms debounce

        return () => clearTimeout(timeoutId);
    }, [searchQuery]);

    // Update dropdown position when showing suggestions
    useEffect(() => {
        if (showSuggestions && inputContainerRef.current) {
            const rect = inputContainerRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
                width: rect.width
            });
        }
    }, [showSuggestions]);

    // Close suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Check if click is outside both the input container and the dropdown
            const isOutsideInput = inputContainerRef.current && !inputContainerRef.current.contains(event.target);
            const isOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(event.target);

            if (isOutsideInput && isOutsideDropdown) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelectPlace = useCallback(async (prediction) => {
        try {
            // Immediately hide suggestions to remove from DOM
            setShowSuggestions(false);
            setSuggestions([]);
            setSearchQuery('');
            setIsSearching(true);

            // Get detailed place information
            const placeDetails = await getPlaceDetails(prediction.place_id);

            // Update local state with coordinates
            if (placeDetails.latitude && placeDetails.longitude) {
                setLatitude(placeDetails.latitude);
                setLongitude(placeDetails.longitude);

                // Notify parent component
                onChange({
                    latitude: placeDetails.latitude,
                    longitude: placeDetails.longitude,
                });
            }
            setIsSearching(false);
        } catch (error) {
            console.error('Error selecting place:', error);
            setIsSearching(false);
        }
    }, [onChange]);

    const handleClear = () => {
        setSearchQuery('');
        setSuggestions([]);
        setShowSuggestions(false);

        setLatitude('');
        setLongitude('');

        // Clean up map and marker
        if (markerRef.current) {
            markerRef.current.setMap(null);
            markerRef.current = null;
        }
        if (mapRef.current) {
            // Clear the map container to prevent DOM conflicts
            if (mapContainerRef.current) {
                mapContainerRef.current.innerHTML = '';
            }
            mapRef.current = null;
        }

        onChange({
            latitude: null,
            longitude: null,
        });
    };

    return (
        <div>
            {/* Search for location */}
            <div style={{ marginBottom: '16px' }}>
                <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151'
                }}>
                    Search Location
                </label>
                <div style={{ position: 'relative' }} ref={inputContainerRef}>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                        <input
                            type="text"
                            className="input"
                            placeholder="Search for a location (e.g., 'Central Park, New York')"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onFocus={() => {
                                if (suggestions.length > 0) {
                                    setShowSuggestions(true);
                                }
                            }}
                            style={{ flex: 1 }}
                        />
                        {isSearching && (
                            <div style={{
                                position: 'absolute',
                                right: '12px',
                                top: '12px',
                                color: '#3b82f6'
                            }}>
                                <LoadingOutlined />
                            </div>
                        )}
                    </div>
                </div>

                {/* Autocomplete Suggestions Dropdown - Rendered via Portal */}
                {showSuggestions && suggestions.length > 0 && dropdownPosition && createPortal(
                    <div
                        ref={dropdownRef}
                        style={{
                            position: 'absolute',
                            top: `${dropdownPosition.top}px`,
                            left: `${dropdownPosition.left}px`,
                            width: `${dropdownPosition.width}px`,
                            backgroundColor: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                            maxHeight: '300px',
                            overflowY: 'auto',
                            zIndex: 10000,
                            marginTop: '-8px'
                        }}>
                        {suggestions.map((prediction) => (
                            <div
                                key={prediction.place_id}
                                onClick={() => handleSelectPlace(prediction)}
                                style={{
                                    padding: '12px 16px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f3f4f6',
                                    transition: 'background-color 0.15s'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f9fafb';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'white';
                                }}
                            >
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '10px'
                                }}>
                                    <EnvironmentOutlined style={{
                                        color: '#6b7280',
                                        fontSize: '16px',
                                        marginTop: '2px'
                                    }} />
                                    <div style={{ flex: 1 }}>
                                        <div style={{
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            color: '#1f2937',
                                            marginBottom: '2px'
                                        }}>
                                            {prediction.main_text}
                                        </div>
                                        {prediction.secondary_text && (
                                            <div style={{
                                                fontSize: '12px',
                                                color: '#6b7280'
                                            }}>
                                                {prediction.secondary_text}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>,
                    document.body
                )}
            </div>

            {/* GPS Coordinates Display */}
            {latitude && longitude && (
                <div style={{ marginBottom: '16px' }}>
                    <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151'
                    }}>
                        GPS Coordinates
                    </label>
                    <div style={{
                        display: 'flex',
                        gap: '8px',
                        alignItems: 'center',
                        padding: '8px 12px',
                        backgroundColor: '#f9fafb',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        fontSize: '13px',
                        color: '#374151'
                    }}>
                        <span>📍 {latitude}, {longitude}</span>
                    </div>
                </div>
            )}

            {/* Interactive Google Map */}
            {latitude && longitude && (
                <div style={{
                    marginTop: '12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    position: 'relative'
                }}>
                    <div
                        ref={mapContainerRef}
                        style={{
                            width: '100%',
                            height: '250px',
                            backgroundColor: '#f3f4f6',
                        }}
                    >
                        {mapLoading && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                                color: '#6b7280',
                                fontSize: '14px'
                            }}>
                                <LoadingOutlined style={{ marginRight: '8px' }} />
                                Loading map...
                            </div>
                        )}
                        {mapError && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                                color: '#dc2626',
                                fontSize: '14px',
                                padding: '20px',
                                textAlign: 'center'
                            }}>
                                ⚠️ {mapError}
                            </div>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={handleClear}
                        className="button is-small is-light"
                        style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            fontSize: '11px',
                            padding: '4px 8px',
                            color: '#dc2626',
                            backgroundColor: 'white',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                            zIndex: 10
                        }}
                    >
                        Clear
                    </button>
                    {!mapLoading && !mapError && (
                        <div style={{
                            position: 'absolute',
                            bottom: '8px',
                            left: '8px',
                            fontSize: '11px',
                            padding: '4px 8px',
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            borderRadius: '4px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                            zIndex: 10,
                            color: '#6b7280'
                        }}>
                            💡 Click or drag marker to adjust location
                        </div>
                    )}
                </div>
            )}

            {!latitude && !longitude && (
                <div style={{
                    fontSize: '12px',
                    color: '#6b7280',
                    marginTop: '4px'
                }}>
                    💡 Tip: Start typing to search for addresses, landmarks, or businesses (min. 3 characters)
                </div>
            )}
        </div>
    );
}

export default GoogleMapsLocationPicker;

